import { validateAndFormatAddress, validateAndLowerCase } from "../utils/validateAndParseAddress.js";
import { AccountType } from "../enums.js";
import { getExternalPlatformIdFromInternalPlatformId, getInternalPlatformIdFromExternalPlatformId, getPlatformIdFromPlatform, } from "../utils/format_utils.js";
import { getVirtualEnvironment } from "../utils/utils.js";
import invariant from "tiny-invariant";
import _ from "lodash";
/**
 * Represents an account on a blockchain platform
 */
export class Account {
    static toAccount(account) {
        const externalPlatformId = account?.platformId ?? account?.chainId;
        const internalPlatformIdFromExternalPlatformId = externalPlatformId
            ? getInternalPlatformIdFromExternalPlatformId(externalPlatformId)
            : undefined;
        const formattedAccountType = validateAndLowerCase(account?.accountType) == AccountType.EOA ? AccountType.EOA : AccountType.SMART_CONTRACT;
        if (!internalPlatformIdFromExternalPlatformId && formattedAccountType == AccountType.SMART_CONTRACT) {
            return undefined;
        }
        return new Account(account.address, formattedAccountType, validateAndLowerCase(account.vmType), internalPlatformIdFromExternalPlatformId);
    }
    constructor(address, type, vmType, platformId) {
        // TODO(felix): add this back
        // TODO(felix): crash if platformId is not valid and type is smart contract
        // invariant(!_.isUndefined(getPlatformFromPlatformId(platformId)), "CHAIN_ID");
        if (type == AccountType.SMART_CONTRACT) {
            invariant(!_.isUndefined(platformId), "PLATFORM_ID");
        }
        const formattedAddress = validateAndFormatAddress(address, vmType);
        invariant(formattedAddress, `${address} is not a valid address.`);
        this.address = formattedAddress;
        this.platformId = platformId ? BigInt(platformId) : undefined;
        this.type = type;
        this.vmType = vmType;
        this.key = Account.key(address, platformId);
        if (this.type == AccountType.SMART_CONTRACT) {
            this.hasSufficientBalanceForGas = true;
            this.usingPaymaster = true;
        }
        else {
            this.hasSufficientBalanceForGas = false;
            this.usingPaymaster = false;
        }
    }
    static key(address, platformId) {
        return platformId ? `${validateAndLowerCase(platformId?.toString())}-${address}` : address;
    }
    static fromAccountModel(account) {
        const internalPlatformIdFromExternalPlatformId = account?.platformId
            ? getInternalPlatformIdFromExternalPlatformId(account.platformId)
            : undefined;
        let formattedAccountType = validateAndLowerCase(account?.accountType) ?? account?.type;
        // @ts-ignore
        // TODO(felix): add this back
        formattedAccountType = ["SCA", "sca"].includes(formattedAccountType)
            ? AccountType.SMART_CONTRACT
            : formattedAccountType;
        if (!internalPlatformIdFromExternalPlatformId && formattedAccountType == AccountType.SMART_CONTRACT) {
            return undefined;
        }
        return new Account(account.address, formattedAccountType, validateAndLowerCase(account.vmType), internalPlatformIdFromExternalPlatformId);
    }
    toAccountModel() {
        const externalPlatformId = this.platformId
            ? getExternalPlatformIdFromInternalPlatformId(this.platformId)
            : "ALL_VM_PLATFORMS";
        return {
            id: this.key,
            address: this.address,
            accountType: this.type == AccountType.EOA ? "eoa" : "sca",
            vmType: this.vmType,
            type: this.type,
            platformId: externalPlatformId,
        };
    }
    toJSON() {
        return {
            address: this.address,
            platformId: this.platformId?.toString(),
            type: this.type,
            vmType: this.vmType,
            key: this.key,
            hasSufficientBalanceForGas: this.hasSufficientBalanceForGas,
            usingPaymaster: this.usingPaymaster,
        };
    }
    equals(other) {
        return this.key == other?.key;
    }
    isSame(address, platformId) {
        return this.key == Account.key(address, platformId);
    }
    isOnPlatform(platform) {
        const platformId = getPlatformIdFromPlatform(platform);
        if (!platformId) {
            return false;
        }
        return this.vmType == getVirtualEnvironment(BigInt(platformId));
    }
    static selectPlatformAccount(platform, accounts) {
        return accounts.find((account) => account.isOnPlatform(platform));
    }
    /**
     * Select the account for a target platform, mirroring backend AccountCluster.selectAccountForPlatform.
     * Prefers exact platformId match, then falls back to same vmType (e.g. OFFCHAIN for Polymarket).
     */
    static selectAccountForPlatform(accounts, targetPlatformId) {
        if (!accounts?.length)
            return undefined;
        const targetVmType = getVirtualEnvironment(targetPlatformId);
        const candidates = accounts.filter((a) => a.platformId == targetPlatformId || (targetVmType && a.vmType === targetVmType));
        return candidates.sort((a, b) => {
            const aExact = a.platformId == targetPlatformId ? 0 : 1;
            const bExact = b.platformId == targetPlatformId ? 0 : 1;
            return aExact - bExact;
        })[0];
    }
}
